import { ErrorBanner } from '@core/components';
import type { DomainError } from '@core/domain';
import { withAppInsightsTracking } from '@core/logging/withAppInsightsTracking';
import { srOnly } from '@core/styles/utilities.css';
import { useCombinedLabels } from '@moderation/hooks';
import { Card, Heading, LoadingSpinner, MultiSelect, Text, TextArea } from '@soteria/design-system-ve';
import { useMemo } from 'react';

import * as styles from './ModerationLabelsPanel.css';

export interface ModerationLabelsPanelProps {
  /**
   * Case ID for the moderation case
   */
  caseId: string;
  /**
   * Community ID to fetch labels for
   */
  communityId: string;
  /**
   * Whether the form is currently submitting
   */
  isSubmitting: boolean;
  /**
   * Errors to display in the error banner
   */
  errors?: DomainError[] | null;
  /**
   * Currently selected label IDs (controlled)
   */
  selectedLabels: string[];
  /**
   * Current comment text (controlled)
   */
  comment: string;
  /**
   * Callback when label selection changes
   */
  onSelectionChange: (labels: string[]) => void;
  /**
   * Callback when comment text changes
   */
  onCommentChange: (comment: string) => void;
}

/**
 * ModerationLabelsPanel Component
 *
 * Accessible moderation labels selection panel using MultiSelect from design system.
 * Operates as a controlled component — state is owned by the parent page.
 *
 * @keyboard-navigation
 * - **Tab**: Navigate between MultiSelect and comment field
 * - **Space/Enter**: Open/close MultiSelect dropdown
 * - **Arrow keys**: Navigate through options in MultiSelect
 * - **Space**: Select/deselect options in MultiSelect
 * - **Escape**: Close MultiSelect dropdown
 *
 * @aria-features
 * - MultiSelect component provides built-in accessibility
 * - Error messages announced with aria-live="assertive"
 * - Loading states indicated with aria-busy
 * - Disabled states indicated with aria-disabled
 *
 * @wcag-compliance
 * - WCAG 2.1 AA compliant
 * - 4.5:1 contrast ratio for all text
 * - 3:1 contrast ratio for UI components
 * - Keyboard accessible without mouse
 * - Screen reader friendly with proper announcements
 */
function ModerationLabelsPanelComponent({
  caseId: _caseId, // Reserved for future use in Phase 3
  communityId,
  isSubmitting,
  errors,
  selectedLabels,
  comment,
  onSelectionChange,
  onCommentChange,
}: ModerationLabelsPanelProps) {
  // Fetch combined labels
  const { labels, loading, errors: fetchErrors } = useCombinedLabels(communityId);

  // Transform labels to MultiSelect format
  const labelOptions = useMemo(
    () =>
      labels.map(label => ({
        id: label.id,
        value: label.id,
        label: label.name,
      })),
    [labels]
  );

  // Combine fetch errors with submission errors
  const allErrors = useMemo(() => {
    const errorList = [...(fetchErrors || []), ...(errors || [])];

    return errorList.length > 0 ? errorList : null;
  }, [fetchErrors, errors]);

  // Loading state
  if (loading) {
    return (
      <Card elevation="low" padding="large">
        <div aria-busy="true" className={styles.panelContent}>
          <div className={styles.loadingContainer}>
            <LoadingSpinner />
            <span className={srOnly}>Loading labels...</span>
          </div>
        </div>
      </Card>
    );
  }

  // Empty state
  if (!loading && labels.length === 0) {
    return (
      <Card elevation="low" padding="large">
        <div className={styles.panelContent}>
          <div className={styles.header}>
            <Heading level="h2">Labels</Heading>
          </div>
          <div className={styles.emptyState}>
            <Text>No labels available for this community.</Text>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card elevation="low" padding="large">
      <div className={styles.panelContent}>
        {/* Header */}
        <div className={styles.header}>
          <Heading id="labels-heading" level="h2">
            Labels
          </Heading>
          <Text aria-describedby="labels-heading" className={styles.caseCount}>
            1 case selected
          </Text>
        </div>

        {/* Error Banner */}
        {allErrors && <ErrorBanner errors={allErrors} title="An error occurred:" />}

        {/* MultiSelect */}
        <div className={styles.multiSelectContainer}>
          <MultiSelect
            searchable
            disabled={isSubmitting}
            options={labelOptions}
            placeholder="Select labels..."
            searchPlaceholder="Search labels..."
            selectedValues={selectedLabels}
            onSelectionChange={onSelectionChange}
          />
        </div>

        {/* Comment Section */}
        <div className={styles.commentSection}>
          <label htmlFor="comment">
            <Text>Comment (Optional)</Text>
          </label>
          <TextArea
            aria-label="Optional comment for moderation action"
            disabled={isSubmitting}
            id="comment"
            placeholder="Add a comment about this moderation action..."
            rows={3}
            value={comment}
            onChange={e => onCommentChange(e.target.value)}
          />
        </div>
      </div>
    </Card>
  );
}

export const ModerationLabelsPanel = withAppInsightsTracking(ModerationLabelsPanelComponent, 'ModerationLabelsPanel');
